import { BASE_API_URL, ECS_BASE_API_URL } from "@/constants/Configs";
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import useAppStore from "@/store/app";

// Standard API response interface
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  statusCode: number;
  message?: string;
}

// Global session management
let globalSessionId: string | null = null;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_API_URL,
  timeout: 20000,
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

// Create ECS axios instance
const ecsClient: AxiosInstance = axios.create({
  baseURL: ECS_BASE_API_URL,
  timeout: 5 * 60 * 60 * 1000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    if (globalSessionId) {
      if (config.data) {
        config.data.sessionId = globalSessionId;
      } else {
        config.data = {
          sessionId: globalSessionId,
        };
      }
    }
    console.log(
      `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`
    );
    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    console.error(
      `❌ API Error: ${error.response?.status} ${error.config?.url}`,
      error.message
    );
    return Promise.reject(error);
  }
);

// ECS Request interceptor
ecsClient.interceptors.request.use(
  (config) => {
    if (globalSessionId) {
      // Only add sessionId to request body for non-GET requests
      // Skip analyze endpoints as they use userId instead of sessionId
      const isAnalyzeEndpoint = config.url?.includes("/analyze");

      if (config.method?.toUpperCase() !== "GET" && !isAnalyzeEndpoint) {
        if (config.data) {
          config.data.sessionId = globalSessionId;
        } else {
          config.data = {
            sessionId: globalSessionId,
          };
        }
      }
    }
    console.log(
      `🚀 ECS Request: ${config.method?.toUpperCase()} ${config.url}`
    );
    return config;
  },
  (error) => {
    console.error("❌ ECS Request Error:", error);
    return Promise.reject(error);
  }
);

// ECS Response interceptor
ecsClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // console.error(
    //   `❌ ECS Error: ${error.response?.status} ${error.config?.url}`,
    //   error.message
    // );

    // // Log detailed error information
    // if (__DEV__ && error.response) {
    //   console.error("Response data:", error.response.data);
    //   console.error("Response headers:", error.response.headers);
    //   console.error("Request data:", error.config?.data);
    //   console.error("Request params:", error.config?.params);
    // }

    return Promise.reject(error);
  }
);

export const setGlobalSessionId = (newSessionId: string) => {
  globalSessionId = newSessionId;
  useAppStore.getState().setHasSession(true);
};

export const getGlobalSessionId = (): string | null => {
  return globalSessionId;
};

export const clearGlobalSessionId = () => {
  globalSessionId = null;
};

const handleResponseSuccess = (response: AxiosResponse) => {
  const { errs, success } = response.data;
  if (errs || success === false) {
    return {
      success: false,
      data: errs,
      statusCode: response.status,
      message: errs[0]?.message,
    };
  }
  return {
    success: true,
    data: response.data,
    statusCode: response.status,
  };
};

const handleResponseError = <T = any>(error: AxiosError) => {
  let message = error.message || "An unknown error occurred";
  const resData = error?.response?.data as any;
  if (resData?.message) {
    message = resData?.message;
  }
  if (resData?.errs?.length > 0) {
    message = resData?.errs[0]?.message;
  }
  return {
    success: false,
    data: error?.response?.data as T,
    statusCode: error?.status || 500,
    message,
  };
};

// Endpoints that can be called without a session (during onboarding/signup)
const UNAUTHENTICATED_ENDPOINTS = [
  "/user/create",
  "/user/login",
  "/auth/sign-in",
  "/auth/forgot-password",
  "/user/password-reset",
  "/finance-account/create-plaid-link-token",
  "/finance-account/create",
  "/unclaimed-property-search/create",
  "/unclaimed-property-search/trigger",
  "/questionnaire/save-progress",
  "/questionnaire/load-progress",
  "/mobile/session-start",
  "/mobile/session-end",
  "/mobile/track-install",
];

// Generic GET function
export const GET = async <T = any>(
  url: string,
  params?: Record<string, any>
): Promise<ApiResponse<T>> => {
  // Guard: Don't make authenticated requests without a session
  const isUnauthenticatedEndpoint = UNAUTHENTICATED_ENDPOINTS.some((endpoint) =>
    url.includes(endpoint)
  );
  console.log(url);
  if (!globalSessionId && !isUnauthenticatedEndpoint) {
    console.warn(`⚠️ Skipping API call to ${url} - no session`);
    return {
      success: false,
      data: null as T,
      statusCode: 401,
      message: "No session - skipping request",
    };
  }

  try {
    const response = await apiClient.get(url, {
      params,
      headers: {
        Cookie: `sessionId=${globalSessionId}`,
      },
    });
    return handleResponseSuccess(response);
  } catch (error: any) {
    const axiosError = error as AxiosError;
    return handleResponseError<T>(axiosError);
  }
};

// Generic POST function
export const POST = async <T = any>(
  url: string,
  data?: any,
  config?: any
): Promise<ApiResponse<T>> => {
  // Guard: Don't make authenticated requests without a session
  const isUnauthenticatedEndpoint = UNAUTHENTICATED_ENDPOINTS.some((endpoint) =>
    url.includes(endpoint)
  );
  if (!globalSessionId && !isUnauthenticatedEndpoint) {
    console.warn(`⚠️ Skipping API call to ${url} - no session`);
    return {
      success: false,
      data: null as T,
      statusCode: 401,
      message: "No session - skipping request",
    };
  }

  try {
    const response = await apiClient.post(url, data, config);
    return handleResponseSuccess(response);
  } catch (error: any) {
    const axiosError = error as AxiosError;
    return handleResponseError<T>(axiosError);
  }
};

// Export the axios instances for direct use if needed
export { apiClient, ecsClient };
export default apiClient;
